import React from 'react';
import { useDocumentsKeyDates } from './hooks/useDocuments';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './scss/DocumentVault.module.scss';
import { Box, Grid, Pagination, Tab, Tabs, Typography } from '@mui/material';
import DocumentListVault from './components/document-list-vault/DocumentListVault';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { DOCUMENTS_PER_PAGE } from '../document-vault/hooks/vaultQueryParams';
import { RootState } from '../../store';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '@mui/material/useMediaQuery';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import Preview from './components/preview-block/Preview';

/** Same breakpoints as `breakpoints.$tablet-mobile` / `$mobile` in scss/settings/_breakpoints.scss. */
const TABLET_MOBILE_QUERY = '(max-width: 991px)';
const MOBILE_QUERY = '(max-width: 767px)';

const DocumentVaultBlock = () => {
  /********* To refactor **************/
  const { t } = useTranslation();
  const documentsLoading = useAppSelector((state: RootState) => state.documents.status);
  const {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    customersLoading,
    suppliersLoading,
    employeesLoading,
    businessProfileLoading,
    documentsCount,
    documentsPage,
    getRequestedPage,
    handleTabChange,
    activeTab,
  } = useDocumentsKeyDates();
  // Calculate total pages
  const totalPages = Math.ceil(documentsCount / DOCUMENTS_PER_PAGE);

  // Handle page change: the page is written to the URL (?page=N) and the documents are fetched from it
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    getRequestedPage(page);
  };

  /********* To refactor later*************/

  const isTabletMobile = useMediaQuery(TABLET_MOBILE_QUERY);
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const showPagination = documents.length > 0;
  const blockTitle = activeTab === 'Subscriptions' ? 'Subscriptions' : 'Documents';

  // Aside (same as the Document Vault page): the Documents / Subscriptions tabs and the
  // "How Key Dates work" overview. Tablet / mobile: it goes above the documents block.
  const renderAside = () => (
    <Box component='aside' id='documentsKeyDatesAside' className={styles.aside}>
      <Box className={styles.aside_section}>
        <Typography id='documentsKeyDatesTypeLabel' component='h2' className={styles.aside_label}>
          {t('labels.type')}
        </Typography>
        <Tabs
          orientation={isTabletMobile ? 'horizontal' : 'vertical'}
          variant='scrollable'
          scrollButtons={isTabletMobile ? 'auto' : false}
          allowScrollButtonsMobile
          value={activeTab}
          onChange={(_, newValue) => {
            handleTabChange(newValue);
          }}
          aria-label='document categories'
          className={styles.category_tabs}
        >
          <Tab
            id='documentsKeyDatesDocumentVaultBlockDocumentsTab'
            label='Documents'
            value='Documents'
          />
          <Tab
            id='documentsKeyDatesDocumentVaultBlockSubscriptionsTab'
            label='Subscriptions'
            value='Subscriptions'
          />
        </Tabs>
      </Box>
      <PreviewTooltip title={<Preview />} arrow placement='right'>
        <button id='documentsKeyDatesHowItWorksBtn' type='button' className={styles.aside_help}>
          <InfoOutlinedIcon fontSize='inherit' className={styles.aside_help_icon} />
          {t('documentVault.howKeyDatesWork')}
        </button>
      </PreviewTooltip>
    </Box>
  );

  // Block header (same as the Document Vault page): the tab title and the pagination.
  const renderHeader = () => (
    <Box id='documentsKeyDatesBlockHeader' className={styles.block_header}>
      <Typography id='documentsKeyDatesBlockTitle' component='h2' className={styles.block_title}>
        {blockTitle}
      </Typography>
      {showPagination && (
        <Box className={styles.header_controls}>
          <Box id='documentsKeyDatesPaginationBox' className={styles.header_pagination}>
            <Typography className={styles.pages_value} variant='caption'>
              {t('labels.pageOfTotal', { page: documentsPage, total: totalPages })}
            </Typography>
            <Pagination
              id='documentsKeyDatesDocumentVaultBlockPagePagination'
              disabled={documentsLoading === 'loading'}
              count={totalPages}
              page={documentsPage}
              onChange={handlePageChange}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className={styles.pagination}
            />
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Grid component='div' container spacing={3} sx={{ marginTop: '16px' }}>
        <Grid className={'controls-grid'} component='div' size={{ xs: 12 }}>
          <Box className={styles.page_description}>{t('messages.keyDatesDescription')}</Box>
          {/* Desktop: the aside sits to the left of the documents block (header with the
              pagination + table). Tablet / mobile: it goes above it. */}
          <Box id='documentsKeyDatesLayout' className={styles.vault_layout}>
            {renderAside()}
            <Box
              id='documentsKeyDatesBlock'
              className={`${styles.vault_block} ${styles.vault_block_joined}`}
            >
              {renderHeader()}
              <Box className={styles.vault_block_content}>
                <DocumentListVault documents={documents} />
                {(documentUploadsLoading === 'loading' ||
                  documentContractsLoading === 'loading' ||
                  customersLoading === 'loading' ||
                  employeesLoading === 'loading' ||
                  businessProfileLoading === 'loading' ||
                  suppliersLoading === 'loading') && <ProgressOverlay position='absolute' />}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default DocumentVaultBlock;
